import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  type CognitoOtpConfig,
  type OtpChallengeState,
  startOtpChallengeSafe,
  verifyOtpSafe,
} from './cognito-auth';
import {
  clearConnectionToken,
  CONNECTION_EXPIRES_AT_KEY,
  ConnectionCompleteError,
  completeConnection,
  isConnectionExpiredLocally,
  isConnectionLinkError,
  MESSAGE_ERROR,
  MESSAGE_SUCCESS,
  OAUTH_ACCESS_TOKEN_KEY,
  closeConnectWindow,
  postToOpener,
  resolveConnectionToken,
  startTestConnection,
  storeConnectionToken,
} from './connect-api';

type Step = 'email' | 'otp' | 'success';

const SIGN_UP_URL = 'https://app.noddpay.com/sign-up';

function ConnectBrandHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <>
      <div class="brand-logo">
        <img
          src="/logos/noddpayc.png"
          alt="NoddPay"
          width={64}
          height={64}
        />
      </div>
      <header class="connect-header">
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </header>
    </>
  );
}

function getCognitoConfig(): CognitoOtpConfig | null {
  const region = import.meta.env.VITE_COGNITO_REGION;
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

  if (!region || !userPoolId || !clientId) {
    return null;
  }

  return { region, userPoolId, clientId };
}

export function ConnectPage() {
  const [connectionToken, setConnectionToken] = useState<string | null>(() =>
    resolveConnectionToken()
  );
  const cognitoConfig = useMemo(() => getCognitoConfig(), []);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [challenge, setChallenge] = useState<OtpChallengeState | null>(null);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [oauthReady, setOauthReady] = useState(false);
  const [showNewConnectionButton, setShowNewConnectionButton] = useState(false);

  const partnersApi = import.meta.env.VITE_PARTNERS_API_URL as string;
  const testPartnerPublicKey = import.meta.env.VITE_TEST_PARTNER_PUBLIC_KEY as string;
  const isDev = import.meta.env.DEV;

  const handleConnectionLinkFailure = (message: string) => {
    clearConnectionToken();
    setConnectionToken(null);
    setShowNewConnectionButton(true);
    setError(message);
    setStep('email');
    setChallenge(null);
    setOtpCode('');
  };

  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('token');
    if (fromUrl) {
      storeConnectionToken(fromUrl);
      sessionStorage.removeItem(CONNECTION_EXPIRES_AT_KEY);
      setConnectionToken(fromUrl);
    }
  }, []);

  useEffect(() => {
    const storedToken = sessionStorage.getItem(OAUTH_ACCESS_TOKEN_KEY);
    if (!storedToken || !connectionToken || !partnersApi) {
      return;
    }

    sessionStorage.removeItem(OAUTH_ACCESS_TOKEN_KEY);
    setAccessToken(storedToken);
    setOauthReady(true);
    setLoading(true);

    completeConnection(partnersApi, connectionToken, storedToken)
      .then(({ data }) => {
        postToOpener(MESSAGE_SUCCESS, {
          userId: data.userId,
          walletAddress: data.walletAddress,
          partnerId: data.partnerId,
          metadata: data.metadata,
        });
        setStep('success');
        window.setTimeout(() => closeConnectWindow(), 1500);
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Connection failed';
        if (err instanceof ConnectionCompleteError && isConnectionLinkError(err.code)) {
          handleConnectionLinkFailure(message);
        } else {
          setError(message);
        }
        postToOpener(MESSAGE_ERROR, { message });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [connectionToken, partnersApi]);

  const handleStartTestConnection = async () => {
    if (!partnersApi || !testPartnerPublicKey) {
      setError('Missing VITE_PARTNERS_API_URL or VITE_TEST_PARTNER_PUBLIC_KEY.');
      return;
    }

    setError(null);
    setShowNewConnectionButton(false);
    setLoading(true);

    try {
      const { connectionToken: freshToken } = await startTestConnection(
        partnersApi,
        testPartnerPublicKey
      );
      setConnectionToken(freshToken);
      setStep('email');
      setOtpCode('');
      setChallenge(null);
      window.history.replaceState(
        null,
        '',
        `/connect?token=${encodeURIComponent(freshToken)}`
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start test connection';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!connectionToken) {
    return (
      <div class="connect">
        <div class="connect-card">
          <ConnectBrandHeader title="Connect NoddPay Wallet" />
          {isDev ? (
            <>
              <p>Missing connection token. Start a test connection for local development.</p>
              <button type="button" disabled={loading} onClick={handleStartTestConnection}>
                {loading ? 'Starting...' : 'Start test connection'}
              </button>
              {error && <p class="error">{error}</p>}
            </>
          ) : (
            <p class="error">Missing connection token. Open this page from the partner app.</p>
          )}
        </div>
      </div>
    );
  }

  if (!cognitoConfig) {
    return (
      <div class="connect">
        <div class="connect-card">
          <ConnectBrandHeader title="Configuration error" />
          <p class="error">
            Missing Cognito env vars (VITE_COGNITO_REGION, VITE_COGNITO_USER_POOL_ID,
            VITE_COGNITO_CLIENT_ID).
          </p>
        </div>
      </div>
    );
  }

  const handleSendCode = async () => {
    if (loading) return;

    setError(null);
    setLoading(true);

    try {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        throw new Error('Email is required');
      }

      const nextChallenge = await startOtpChallengeSafe(cognitoConfig, trimmedEmail);
      setChallenge(nextChallenge);
      if (nextChallenge.accessToken) {
        setAccessToken(nextChallenge.accessToken);
      }
      setStep('otp');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send code';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (loading) return;

    if (!connectionToken) {
      setError('Missing connection token.');
      return;
    }

    if (isConnectionExpiredLocally()) {
      handleConnectionLinkFailure(
        'Link expired. In dev, click "Start new test connection".'
      );
      return;
    }

    setError(null);
    setShowNewConnectionButton(false);
    setLoading(true);

    try {
      let token = accessToken.trim();

      if (!token && challenge) {
        token = await verifyOtpSafe(cognitoConfig, challenge, otpCode);
        setAccessToken(token);
      }

      if (!token) {
        throw new Error('Verification code is required');
      }

      const { data } = await completeConnection(partnersApi, connectionToken, token);

      postToOpener(MESSAGE_SUCCESS, {
        userId: data.userId,
        walletAddress: data.walletAddress,
        partnerId: data.partnerId,
        metadata: data.metadata,
      });

      setStep('success');
      window.setTimeout(() => closeConnectWindow(), 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Connection failed';
      if (err instanceof ConnectionCompleteError && isConnectionLinkError(err.code)) {
        handleConnectionLinkFailure(message);
      } else {
        setError(message);
      }
      postToOpener(MESSAGE_ERROR, { message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div class="connect">
      <div class="connect-card">
        <ConnectBrandHeader
          title="Connect NoddPay Wallet"
          subtitle="Link your NoddPay account to continue with the partner application."
        />

        {isDev && showNewConnectionButton && (
          <button type="button" disabled={loading} onClick={handleStartTestConnection}>
            {loading ? 'Starting...' : 'Start new test connection'}
          </button>
        )}

        {oauthReady && step !== 'success' && (
          <button type="button" disabled={loading} onClick={handleConnect}>
            {loading ? 'Connecting...' : 'Connect wallet'}
          </button>
        )}

        {step === 'email' && !oauthReady && (
          <div class="connect-form">
            <div class="field">
              <label for="email">
                Email <span class="required" aria-hidden="true">*</span>
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                required
              />
            </div>
            <button type="button" disabled={loading} onClick={handleSendCode}>
              {loading ? 'Sending...' : 'Send verification code'}
            </button>
          </div>
        )}

        {step === 'otp' && (
          <div class="connect-form">
            <p class="hint">Code sent to {challenge?.email ?? email}</p>
            <div class="field">
              <label for="otp">
                Verification code <span class="required" aria-hidden="true">*</span>
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter 6-digit code"
                value={otpCode}
                onInput={(e) => setOtpCode((e.target as HTMLInputElement).value)}
                required
              />
            </div>
            <button type="button" disabled={loading} onClick={handleConnect}>
              {loading ? 'Connecting...' : 'Connect wallet'}
            </button>
            <button
              type="button"
              class="link-button"
              disabled={loading}
              onClick={() => {
                setStep('email');
                setOtpCode('');
                setChallenge(null);
              }}
            >
              Use a different email
            </button>
          </div>
        )}

        {step === 'success' && <p class="success">Your wallet is connected.</p>}

        {step !== 'success' && (
          <p class="signup-prompt">
            Don't have an account?{' '}
            <a href={SIGN_UP_URL} target="_blank" rel="noopener noreferrer">
              Sign up
            </a>
          </p>
        )}

        {error && <p class="error">{error}</p>}
      </div>
    </div>
  );
}
